import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import type {
  CompleteEducationBlacklist,
  CompleteEducationDecision,
} from "@/features/education-management/types/complete-education.types";
import { ExcuseReason } from "@/features/education-management/types/education-management.types";

import styles from "@/features/education-catalog/components/CompleteEducationModal.module.scss";

interface PendingEnrollment {
  enrollmentId: string;
  userId: string;
  firstName: string;
  lastName: string;
  status: number;
  virtualClassroomId: string | null;
  virtualClassroomName: string | null;
}

interface CloseEducationModalProps {
  isOpen: boolean;
  educationId: string | undefined;
  onClose: () => void;
  onClosed: () => void;
}

type DecisionMap = Record<string, CompleteEducationDecision>;
type ExcuseMap = Record<string, ExcuseReason | undefined>;

const EXCUSE_REASON_VALUES: ExcuseReason[] = [
  ExcuseReason.HEALTH,
  ExcuseReason.ANNUAL_LEAVE,
  ExcuseReason.BUSINESS_TRAVEL,
  ExcuseReason.OFFICIAL_DUTY,
  ExcuseReason.FAMILY_EMERGENCY,
  ExcuseReason.OTHER,
];

/**
 * Eğitimi kapat (sene sonu) diyaloğu.
 *
 * - Mount'ta `pending-close-summary` çağrılır.
 * - Pending boşsa tek butonla kapatma.
 * - Pending varsa: classroom-bazlı uyarı tablosu + "Tümünü Tamamlamadı işaretle"
 *   hızlı aksiyonu + per-row decision dropdown.
 */
const CloseEducationModal: React.FC<CloseEducationModalProps> = ({
  isOpen,
  educationId,
  onClose,
  onClosed,
}) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pending, setPending] = useState<PendingEnrollment[]>([]);
  const [decisions, setDecisions] = useState<DecisionMap>({});
  const [excuseReasons, setExcuseReasons] = useState<ExcuseMap>({});
  const [blacklist, setBlacklist] = useState<CompleteEducationBlacklist>({
    reason: 1,
    startDate: "",
    endDate: "",
    description: "",
  });

  const excuseReasonOptions = useMemo(
    () =>
      EXCUSE_REASON_VALUES.map((value) => ({
        label: t(`attendanceModal.excuseReasons.${value}`, String(value)),
        value: String(value),
      })),
    [t],
  );

  const fetchPending = React.useCallback(() => {
    if (!educationId) return;
    setIsLoading(true);
    educationManagementApi
      .getPendingCloseSummary(educationId)
      .then((res) => {
        setPending(res.pending);
        setDecisions({});
        setExcuseReasons({});
      })
      .catch(() => {
        toast.error(t("closeEducation.loadError"));
      })
      .finally(() => setIsLoading(false));
  }, [educationId, t]);

  useEffect(() => {
    if (!isOpen) return;
    fetchPending();
  }, [isOpen, fetchPending]);

  const counts = useMemo(() => {
    let completed = 0;
    let notCompleted = 0;
    let blacklisted = 0;
    let pendingCount = 0;
    for (const e of pending) {
      const d = decisions[e.enrollmentId];
      if (d === "COMPLETED") completed++;
      else if (d === "NOT_COMPLETED") notCompleted++;
      else if (d === "BLACKLISTED") blacklisted++;
      else pendingCount++;
    }
    return {
      completed,
      notCompleted,
      blacklisted,
      pending: pendingCount,
    };
  }, [pending, decisions]);

  const hasBlacklisted = counts.blacklisted > 0;
  // Pending listesi backend'den geldiğinden, "ne yapacağız" açık bırakma da
  // serbest — user aslında forceCloseAsNotCompleted ile gönderebilir.
  const allDecided = counts.pending === 0;

  const groupedByClassroom = useMemo(() => {
    const groups = new Map<
      string | null,
      { name: string | null; rows: PendingEnrollment[] }
    >();
    for (const e of pending) {
      const key = e.virtualClassroomId ?? null;
      const existing = groups.get(key);
      if (existing) existing.rows.push(e);
      else groups.set(key, { name: e.virtualClassroomName, rows: [e] });
    }
    return Array.from(groups.entries()).map(([id, val]) => ({
      classroomId: id,
      classroomName: val.name,
      rows: val.rows,
    }));
  }, [pending]);

  const setDecision = (id: string, decision: CompleteEducationDecision) => {
    setDecisions((prev) => ({ ...prev, [id]: decision }));
    if (decision !== "NOT_COMPLETED") {
      setExcuseReasons((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const setExcuseReason = (id: string, value: ExcuseReason | undefined) => {
    setExcuseReasons((prev) => {
      const next = { ...prev };
      if (value == null) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const markAllNotCompleted = () => {
    setDecisions((prev) => {
      const next = { ...prev };
      for (const e of pending) next[e.enrollmentId] = "NOT_COMPLETED";
      return next;
    });
  };

  const missingExcuseCount = useMemo(() => {
    let count = 0;
    for (const e of pending) {
      if (
        decisions[e.enrollmentId] === "NOT_COMPLETED" &&
        excuseReasons[e.enrollmentId] == null
      ) {
        count++;
      }
    }
    return count;
  }, [pending, decisions, excuseReasons]);

  const validateBlacklistForm = (): string | null => {
    if (!hasBlacklisted) return null;
    if (!blacklist.startDate || !blacklist.endDate) {
      return t("completeEducation.blacklist.datesRequired");
    }
    if (new Date(blacklist.startDate) >= new Date(blacklist.endDate)) {
      return t("completeEducation.blacklist.dateRangeInvalid");
    }
    return null;
  };

  const handleSubmit = async ({
    forceCloseAsNotCompleted,
  }: { forceCloseAsNotCompleted?: boolean } = {}) => {
    if (!educationId) return;
    if (missingExcuseCount > 0) {
      toast.error(
        t("attendance.missingExcuseReason", t("attendanceLabels.missingExcuses"), {
          count: missingExcuseCount,
        }),
      );
      return;
    }
    const blacklistError = validateBlacklistForm();
    if (blacklistError) {
      toast.error(blacklistError);
      return;
    }

    const decided = Object.entries(decisions).map(([enrollmentId, decision]) => {
      const excuse = excuseReasons[enrollmentId];
      if (decision === "NOT_COMPLETED" && excuse != null) {
        return { enrollmentId, decision, excuseReason: excuse };
      }
      return { enrollmentId, decision };
    });

    const body: {
      decisions?: typeof decided;
      blacklist?: CompleteEducationBlacklist;
      forceCloseAsNotCompleted?: boolean;
    } = {};
    if (decided.length > 0) body.decisions = decided;
    if (forceCloseAsNotCompleted) body.forceCloseAsNotCompleted = true;
    if (hasBlacklisted) {
      body.blacklist = {
        reason: blacklist.reason,
        startDate: new Date(blacklist.startDate).toISOString(),
        endDate: new Date(blacklist.endDate).toISOString(),
        description: blacklist.description?.trim() || undefined,
      };
    }

    setIsSubmitting(true);
    try {
      await educationManagementApi.closeEducation(educationId, body);
      toast.success(t("closeEducation.success"));
      onClosed();
      onClose();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t("closeEducation.submitError");
      toast.error(msg);
      // 409 → açık kayıtlar değişmiş olabilir; listeyi yenile.
      if (status === 409) fetchPending();
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderHeader = () => (
    <div className={styles.header}>
      <div>
        <h3 className={styles.title}>{t("closeEducation.heading")}</h3>
        <p className={styles.subtitle}>
          {pending.length === 0
            ? t("closeEducation.readyMessage")
            : t("closeEducation.pendingWarning", { count: pending.length })}
        </p>
      </div>
      {pending.length > 0 && (
        <div className={styles.counts}>
          <span className={`${styles.countPill} ${styles.completed}`}>
            {t("completeEducation.counts.completed", { n: counts.completed })}
          </span>
          <span className={`${styles.countPill} ${styles.notCompleted}`}>
            {t("completeEducation.counts.notCompleted", { n: counts.notCompleted })}
          </span>
          <span className={`${styles.countPill} ${styles.blacklisted}`}>
            {t("completeEducation.counts.blacklisted", { n: counts.blacklisted })}
          </span>
          {counts.pending > 0 && (
            <span className={`${styles.countPill} ${styles.pending}`}>
              {t("completeEducation.counts.pending", { n: counts.pending })}
            </span>
          )}
        </div>
      )}
    </div>
  );

  const footer = (
    <>
      <Button variant="text" color="secondary" onClick={onClose} type="button">
        {t("common.cancel")}
      </Button>
      {pending.length > 0 && counts.pending > 0 && (
        <Button
          variant="outlined"
          color="warning"
          type="button"
          onClick={() => handleSubmit({ forceCloseAsNotCompleted: true })}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {t("closeEducation.forceCloseAsNotCompleted")}
        </Button>
      )}
      <Button
        variant="contained"
        color="primary"
        type="button"
        onClick={() => handleSubmit()}
        loading={isSubmitting}
        disabled={isSubmitting || (pending.length > 0 && !allDecided)}
      >
        {pending.length === 0
          ? t("closeEducation.confirmReady")
          : t("closeEducation.confirm")}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={t("closeEducation.title")}
      loading={isSubmitting}
      customFooter={footer}
    >
      <div className={styles.container}>
        {renderHeader()}

        {isLoading ? (
          <div className={styles.loadingState}>{t("common.loading")}</div>
        ) : pending.length === 0 ? (
          <div className={styles.emptyState}>
            {t("closeEducation.readyEmptyState")}
          </div>
        ) : (
          <>
            <div className={styles.toolbar}>
              <span className={styles.toolbarLabel}>
                {t("closeEducation.bulkLabel")}
              </span>
              <div className={styles.toolbarActions}>
                <Button
                  variant="outlined"
                  color="warning"
                  type="button"
                  onClick={markAllNotCompleted}
                >
                  {t("closeEducation.markAllNotCompleted")}
                </Button>
              </div>
            </div>

            {groupedByClassroom.map((g) => (
              <div
                key={g.classroomId ?? "unassigned"}
                className={styles.groupBlock}
              >
                <header className={styles.groupHeader}>
                  <span className={styles.groupTitle}>
                    {g.classroomName ?? t("completeEducation.group.unassigned")}
                  </span>
                  <span className={styles.groupMeta}>
                    {t("completeEducation.group.count", { n: g.rows.length })}
                  </span>
                </header>
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t("completeEducation.col.name")}</th>
                        <th>{t("closeEducation.col.status")}</th>
                        <th>{t("completeEducation.col.decision")}</th>
                        <th>{t("attendance.excuseReason")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((e) => {
                        const d = decisions[e.enrollmentId];
                        return (
                          <tr key={e.enrollmentId}>
                            <td className={styles.nameCell}>
                              {e.firstName} {e.lastName}
                            </td>
                            <td className={styles.metaCell}>{e.status}</td>
                            <td>
                              <div className={styles.segmented}>
                                <button
                                  type="button"
                                  className={`${styles.segButton} ${
                                    d === "COMPLETED"
                                      ? `${styles.active} ${styles.completed}`
                                      : ""
                                  }`}
                                  onClick={() =>
                                    setDecision(e.enrollmentId, "COMPLETED")
                                  }
                                >
                                  {t("completeEducation.actions.completed")}
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.segButton} ${
                                    d === "NOT_COMPLETED"
                                      ? `${styles.active} ${styles.notCompleted}`
                                      : ""
                                  }`}
                                  onClick={() =>
                                    setDecision(e.enrollmentId, "NOT_COMPLETED")
                                  }
                                >
                                  {t("completeEducation.actions.notCompleted")}
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.segButton} ${
                                    d === "BLACKLISTED"
                                      ? `${styles.active} ${styles.blacklisted}`
                                      : ""
                                  }`}
                                  onClick={() =>
                                    setDecision(e.enrollmentId, "BLACKLISTED")
                                  }
                                >
                                  {t("completeEducation.actions.blacklisted")}
                                </button>
                              </div>
                            </td>
                            <td>
                              {d === "NOT_COMPLETED" ? (
                                (() => {
                                  const excuse = excuseReasons[e.enrollmentId];
                                  const isMissing = excuse == null;
                                  return (
                                    <div className={styles.excuseCell}>
                                      <Dropdown
                                        options={excuseReasonOptions}
                                        value={
                                          excuse != null ? String(excuse) : null
                                        }
                                        selectedLabel={
                                          excuse != null
                                            ? t(
                                                `attendanceModal.excuseReasons.${excuse}`,
                                                String(excuse),
                                              )
                                            : undefined
                                        }
                                        placeholder={t(
                                          "attendance.excuseReasonPlaceholder",
                                        )}
                                        onSelect={(v) => {
                                          const raw = Array.isArray(v)
                                            ? v[0]
                                            : v;
                                          const num = Number(raw);
                                          setExcuseReason(
                                            e.enrollmentId,
                                            !Number.isNaN(num) &&
                                              num >= 0 &&
                                              num <= 5
                                              ? (num as ExcuseReason)
                                              : undefined,
                                          );
                                        }}
                                      />
                                      {isMissing && (
                                        <span className={styles.excuseError}>
                                          {t("attendance.excuseRequired")}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()
                              ) : (
                                <span className={styles.excuseMuted}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {hasBlacklisted && (
              <div className={styles.blacklistSection}>
                <h4 className={styles.blacklistTitle}>
                  {t("completeEducation.blacklist.title", {
                    n: counts.blacklisted,
                  })}
                </h4>
                <p className={styles.blacklistHint}>
                  {t("completeEducation.blacklist.hint")}
                </p>
                <div className={styles.blacklistGrid}>
                  <label className={styles.field}>
                    {t("completeEducation.blacklist.reason")}
                    <select
                      value={blacklist.reason}
                      onChange={(ev) =>
                        setBlacklist((b) => ({
                          ...b,
                          reason: Number(ev.target.value) as 1 | 2,
                        }))
                      }
                    >
                      <option value={1}>
                        {t("completeEducation.blacklist.reasonAbsence")}
                      </option>
                      <option value={2}>
                        {t("completeEducation.blacklist.reasonFailure")}
                      </option>
                    </select>
                  </label>
                  <label className={styles.field}>
                    {t("completeEducation.blacklist.startDate")}
                    <input
                      type="date"
                      value={blacklist.startDate}
                      onChange={(ev) =>
                        setBlacklist((b) => ({ ...b, startDate: ev.target.value }))
                      }
                    />
                  </label>
                  <label className={styles.field}>
                    {t("completeEducation.blacklist.endDate")}
                    <input
                      type="date"
                      value={blacklist.endDate}
                      onChange={(ev) =>
                        setBlacklist((b) => ({ ...b, endDate: ev.target.value }))
                      }
                    />
                  </label>
                  <label className={`${styles.field} ${styles.fieldFull}`}>
                    {t("completeEducation.blacklist.description")}
                    <textarea
                      rows={2}
                      value={blacklist.description ?? ""}
                      onChange={(ev) =>
                        setBlacklist((b) => ({
                          ...b,
                          description: ev.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default CloseEducationModal;
